import { MapPin, ArrowDownUp } from 'lucide-react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import HandsClappingImage from 'src/features/business/referrals/assets/send-referral/hands-clapping.png'
import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { useEventStore } from 'src/shared/store/event-store'
import { formatPrintedReferralAddress } from 'src/shared/utils/format-field-value'

const ReferralConfirmationDialog = () => {
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const resetSendReferralStore = useSendReferralStore((state) => state.resetSendReferralStore)
  const receiver = useSendReferralStore((state) => state.receiver)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId

  useEffect(() => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_end',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'view_confirmation',
          flowId,
        },
      })
    }
  }, [currentUser, sessionId, flowId])

  if (!receiver || !selectedReferral) {
    return null //TODO: handle this better
  }

  return (
    <div className={`flex flex-col ${isMobile ? 'gap-6 p-3' : 'gap-9 p-4'}`}>
      <header className="flex flex-col items-center gap-3">
        <div className={`mb-4 flex justify-center`}>
          <img src={HandsClappingImage} alt="Hands clapping" className={isMobile ? 'size-28' : 'size-36'} />
        </div>
        <div className="flex flex-col items-center gap-3">
          <h2 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-semibold text-primary-foreground`}>
            Congratulations!
          </h2>
          <p className={`text-center ${isMobile ? 'text-lg' : 'text-xl'} font-medium text-primary-foreground`}>
            You sent a referral to {receiver.fullName}
          </p>
        </div>
      </header>
      <section>
        <div className="mt-4 rounded-lg bg-accent p-4 text-center text-primary shadow-lg">
          <p className="font-semibold">{`${selectedReferral?.clientFirstName} ${selectedReferral?.clientLastName}`}</p>
          <div className="mt-2 flex items-center justify-center">
            <MapPin className={`mr-1 ${isMobile ? 'size-3' : 'size-4'} text-muted`} />
            <span className={isMobile ? 'text-sm' : ''}>{formatPrintedReferralAddress(selectedReferral)}</span>
          </div>
        </div>
        <div className="my-4 flex justify-center">
          <ArrowDownUp className={`${isMobile ? 'size-8' : 'size-10'} text-secondary`} />
        </div>
        <div className="mt-5 flex items-center justify-start rounded-lg bg-accent p-4 text-primary shadow-lg">
          <ProfileAvatar userId={receiver?.id} size={isMobile ? 45 : 55} />
          <div className="ml-2">
            <p className="font-semibold">{`${receiver?.firstName} ${receiver?.lastName}`}</p>
            <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted`}>
              {professionMap[receiver.profession].label}
            </p>
          </div>
        </div>
      </section>

      <Button
        onClick={() => {
          resetSendReferralStore()
          navigate(paths.referrals.detail.referral.getHref(selectedReferral.id.toString()))
        }}
        variant="secondaryInverse"
        className="w-full text-primary"
      >
        View Referral
      </Button>
    </div>
  )
}

export default ReferralConfirmationDialog
